/* eslint-disable no-console -- all console calls in this file are dev-only, guarded by isDev() */
/**
 * IMS Tracking Service
 *
 * Singleton class that sends analytics events to the IMS Tracking API.
 *
 * ── Architecture ───────────────────────────────────────────────────────────
 *  • Consent gate is INSIDE the service.
 *    Callers never check consent before calling track(). The service decides.
 *
 *  • Adding a new event type = 3 steps:
 *    1. Add the event name to EVENTS (const object below)
 *    2. Map it to a consent category in EVENT_CONSENT
 *    3. Call tracker.track(EVENTS.YOUR_EVENT, { ...data }) at the right place
 *
 *  • Silent fail everywhere — tracking errors NEVER affect the app.
 *
 *  • In dev, payloads are logged to the console as
 *    "[Tracking] Sending payload: {...}" for easy debugging.
 *
 * ── Consent categories ─────────────────────────────────────────────────────
 *  necessary    → always sent, no opt-in required (session_init, interactions)
 *  performance  → only if performanceCookies consent given (visit, session_duration, AR)
 *  marketing    → only if marketingCookies consent given (CTA clicks, UTM)
 *
 * ── Usage ──────────────────────────────────────────────────────────────────
 *  // One-time init (in useTracking hook, after config + consent known):
 *  tracker.init({ brandId, marketId, modelId, performance, marketing });
 *
 *  // Fire events anywhere:
 *  tracker.track(EVENTS.MODEL_SELECTED, { modelId: 'q3sportback' });
 *  tracker.track(EVENTS.COLOR_CHANGED,  { colorId: 'mythos-black' });
 *  tracker.track(EVENTS.CTA_WEBSITE);   // silently dropped if no marketing consent
 *
 *  // Update consent after the user changes cookie settings:
 *  tracker.updateConsent({ performance: true, marketing: false });
 *
 * @see docs/ims-brand-frontends/09-cookie-consent-tracking.md
 */

// ---------------------------------------------------------------------------
// Event catalogue — the Single Source of Truth for all event names
// ---------------------------------------------------------------------------

/**
 * All trackable event names.
 * To add a new event: add it here + map consent in EVENT_CONSENT below.
 */
export const EVENTS = {
  // ── necessary — always tracked (no GDPR blocker) ──────────────────────
  SESSION_INIT: 'session_init',
  MODEL_SELECTED: 'model_selected',
  COLOR_CHANGED: 'color_changed',
  RIM_CHANGED: 'rim_changed',
  VIEWPOINT_CHANGED: 'viewpoint_changed',
  DOOR_OPENED: 'door_opened',
  INTERIOR_CHANGED: 'interior_changed',

  // ── performance — requires performanceCookies consent ─────────────────
  VISIT: 'visit',
  SESSION_DURATION: 'session_duration',
  AR_MODE: 'ar_mode',
  AR_FAILED: 'ar_failed',

  // ── marketing — requires marketingCookies consent ──────────────────────
  CTA_WEBSITE: 'cta_website',
  CTA_TESTDRIVE: 'cta_testdrive',
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];
type ConsentCategory = 'necessary' | 'performance' | 'marketing';

/** Maps each event to the consent category it requires. */
const EVENT_CONSENT: Record<EventName, ConsentCategory> = {
  session_init: 'necessary',
  model_selected: 'necessary',
  color_changed: 'necessary',
  rim_changed: 'necessary',
  viewpoint_changed: 'necessary',
  door_opened: 'necessary',
  interior_changed: 'necessary',
  visit: 'performance',
  session_duration: 'performance',
  ar_mode: 'performance',
  ar_failed: 'performance',
  cta_website: 'marketing',
  cta_testdrive: 'marketing',
};

/**
 * Events that count as explicit user interactions.
 * Automatic/background events (session_init, visit, session_duration,
 * ar_failed) are excluded.
 * Used to populate the `interactions` field in session_duration.
 */
const INTERACTION_EVENTS = new Set<EventName>([
  'model_selected',
  'color_changed',
  'rim_changed',
  'viewpoint_changed',
  'door_opened',
  'interior_changed',
  'ar_mode',
  'cta_website',
  'cta_testdrive',
]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TrackerInitParams {
  brandId: string;
  marketId: string;
  modelId: string;
  performance: boolean;
  marketing: boolean;
}

/** Exact payload shape the IMS Tracking API expects. */
interface TrackingPayload {
  brandid: string;
  marketid: string;
  modelid: string;
  sessionid: string;
  userid: string;
  event: string;
  campaign?: string;
  preset?: string;
  timestamp: number;
  page: string;
  data?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_ENDPOINT =
  (import.meta.env.VITE_TRACKING_ENDPOINT as string | undefined) ??
  'https://bwftutp3h8.execute-api.eu-west-1.amazonaws.com/prod/imsTrack';
const USER_ID_KEY = 'ims-userid';

// Retry settings for tracking requests.
//
// 504 = API Gateway timeout caused by a Lambda cold start.
//
// Root cause: Lambda cold start takes ~60 s, but API Gateway has a hard
// 29 s max integration timeout — so the first request after a cold period
// will always 504. After the 29 s timeout the Lambda continues warming up
// in the background and is ready after ~60 s total.
//
// Strategy: 1 retry after 35 s.
//   cold start fires at t=0  → 504 at t=29s
//   Lambda ready at  t≈60s
//   retry fires at   t=64s  → hits warm Lambda → succeeds
//
// Tracking is fire-and-forget — no user-visible delay from the retry wait.
//
// ── TODO: Remove / simplify once backend migrates to ECS ───────────────────
//
//   When the backend runs as an always-on ECS container there are no cold
//   starts, so the retry workaround below becomes unnecessary.
//
//   Migration checklist (client-side):
//   1. Update API_ENDPOINT above to point at the new ECS service URL.
//   2. Confirm the new endpoint accepts the same JSON payload shape
//      (TrackingPayload interface — brandid, marketid, modelid, etc.).
//   3. Confirm auth mechanism — if the ECS service uses a different auth
//      scheme (e.g. x-api-key instead of Basic Auth), update AUTH_HEADER.
//   4. Reduce / remove the retry:
//        const SEND_MAX_RETRIES    = 0;   // or delete the retry logic entirely
//        const SEND_RETRY_DELAY_MS = 0;
//   5. Run `pnpm test --run` — all 504-retry tests should be removed or
//      updated to match the new zero-retry behaviour.
//
const SEND_MAX_RETRIES = 1;
const SEND_RETRY_DELAY_MS = 35_000;

// Auth credentials are loaded from environment variables.
// Set VITE_TRACKING_USER and VITE_TRACKING_PASS in packages/brand-audi/.env.local (dev)
// and in your CI / Firebase Hosting environment (production).
// See packages/brand-audi/.env.example for the required variable names.
const AUTH_HEADER = (() => {
  const user = import.meta.env.VITE_TRACKING_USER as string | undefined;
  const pass = import.meta.env.VITE_TRACKING_PASS as string | undefined;
  if (user && pass) return 'Basic ' + btoa(`${user}:${pass}`);
  // Development fallback — remove once all environments have the env vars set.
  return 'Basic ' + btoa('ims-user:45G_%sdfuysd_');
})();

// ---------------------------------------------------------------------------
// TrackingService class
// ---------------------------------------------------------------------------

class TrackingService {
  private brandId = '';
  private marketId = '';
  private modelId = '';
  private sessionId = '';
  private userId = '';
  private campaign = '';
  private preset = '';

  private performanceConsent = false;
  private marketingConsent = false;
  private initialized = false;

  /** Counts explicit user interactions within the current session segment. */
  private interactionCount = 0;

  // --------------------------------------------------------------------------
  // Public API
  // --------------------------------------------------------------------------

  /**
   * Initialize (or re-initialize) the tracker.
   *
   * Call once when both config and consent are known.
   * Safe to call again when consent changes — keeps the existing sessionId/
   * userId so no data gap occurs.
   */
  init(params: TrackerInitParams): void {
    this.brandId = params.brandId;
    this.marketId = params.marketId;
    this.modelId = params.modelId;
    this.performanceConsent = params.performance;
    this.marketingConsent = params.marketing;

    // Read URL params directly — avoids polluting the app store with
    // tracking-specific concerns.
    // NOTE: Use globalThis.location (not window.location) so that
    //       vi.stubGlobal('location', ...) works in tests.
    const search = globalThis.location?.search ?? '';
    const urlParams = new URLSearchParams(search);
    this.campaign = urlParams.get('campaign') ?? '';
    this.preset = urlParams.get('preset') ?? '';

    // Session ID — fresh per browser-tab lifecycle (not persisted).
    if (!this.sessionId) {
      this.sessionId =
        typeof crypto !== 'undefined'
          ? crypto.randomUUID()
          : `session-${Date.now()}-${Math.random()}`;
    }

    // User ID — persistent across sessions in localStorage.
    // NOTE: Use globalThis.localStorage so vi.stubGlobal overrides work.
    const ls = globalThis.localStorage;
    if (ls) {
      let uid = ls.getItem(USER_ID_KEY);
      if (!uid) {
        uid =
          typeof crypto !== 'undefined'
            ? crypto.randomUUID()
            : `user-${Date.now()}-${Math.random()}`;
        ls.setItem(USER_ID_KEY, uid);
      }
      this.userId = uid;
    }

    this.initialized = true;

    // ── Dev logging ──────────────────────────────────────────────────────
    if (this.isDev()) {
      console.group('%c[IMS Tracking] ✅ Initialized', 'color: #4ade80; font-weight: bold');
      console.table({
        brandId: this.brandId,
        marketId: this.marketId,
        modelId: this.modelId,
        sessionId: this.sessionId,
        userId: this.userId,
        campaign: this.campaign || '(none)',
        preset: this.preset || '(none)',
      });
      console.log(
        'Consent: %c necessary ✅ %c performance %s %c marketing %s',
        'color: #4ade80',
        this.performanceConsent ? 'color: #4ade80' : 'color: #f87171',
        this.performanceConsent ? '✅' : '❌',
        this.marketingConsent ? 'color: #4ade80' : 'color: #f87171',
        this.marketingConsent ? '✅' : '❌',
      );
      console.groupEnd();
    }
  }

  /**
   * Update consent mid-session (user reopens cookie sheet and changes toggles).
   * Does not re-send session_init or visit.
   */
  updateConsent(params: { performance: boolean; marketing: boolean }): void {
    const changed =
      this.performanceConsent !== params.performance || this.marketingConsent !== params.marketing;

    this.performanceConsent = params.performance;
    this.marketingConsent = params.marketing;

    if (this.isDev() && changed) {
      console.log(
        '%c[IMS Tracking] 🔄 Consent updated  %c performance %s %c marketing %s',
        'color: #60a5fa; font-weight: bold',
        params.performance ? 'color: #4ade80' : 'color: #f87171',
        params.performance ? '✅' : '❌',
        params.marketing ? 'color: #4ade80' : 'color: #f87171',
        params.marketing ? '✅' : '❌',
      );
    }
  }

  /**
   * Update the active model ID — call when the user navigates to a different model.
   */
  updateModelId(modelId: string): void {
    this.modelId = modelId;
  }

  /**
   * Fire a tracking event.
   *
   * The service checks consent automatically. If the event's required consent
   * category has not been granted, the call is a silent no-op.
   *
   * @param event - One of the EVENTS constants.
   * @param data  - Optional event-specific payload (merged into `data` field).
   */
  track(event: EventName, data?: Record<string, unknown>): void {
    if (!this.initialized) {
      if (this.isDev()) {
        console.warn(
          `%c[IMS Tracking] ⏳ ${event} → QUEUED (tracker not initialized yet)`,
          'color: #fbbf24',
        );
      }
      return;
    }

    if (!this.hasConsent(event)) {
      if (this.isDev()) {
        const needed = EVENT_CONSENT[event];
        console.log(
          `%c[IMS Tracking] 🚫 ${event} → BLOCKED %c(requires: ${needed})`,
          'color: #f87171; font-weight: bold',
          'color: #94a3b8',
        );
      }
      return;
    }

    const payload = this.buildPayload(event, data);
    this.send(payload);

    // Count explicit user interactions for session_duration reporting.
    if (INTERACTION_EVENTS.has(event)) {
      this.interactionCount++;
    }
  }

  /**
   * Send session_duration.
   *
   * @param data.duration  Active seconds (excluding tab-invisible time).
   *
   * The `interactions` count is managed internally — auto-incremented by
   * `track()` for every event in INTERACTION_EVENTS, then reset after send.
   */
  trackSessionDuration(data: { duration: number }): void {
    if (!this.initialized || !this.performanceConsent) return;

    const interactions = this.interactionCount;
    this.interactionCount = 0; // reset for next segment (tab re-focus)

    const payload = this.buildPayload(EVENTS.SESSION_DURATION, {
      duration: data.duration,
      interactions,
    });

    // sendBeacon doesn't support custom headers — always use fetch so the
    // Authorization header is included in the request.
    this.send(payload);

    if (this.isDev()) {
      console.groupCollapsed(
        '%c[IMS Tracking] ⏱ session_duration → FETCH',
        'color: #a78bfa; font-weight: bold',
      );
      console.log('Duration:', data.duration, 's');
      console.log('Interactions:', interactions);
      console.log('Payload:', payload);
      console.groupEnd();
    }
  }

  /**
   * Reset all internal state. FOR TESTING ONLY.
   * Also clears the localStorage user ID so tests start fresh.
   */
  _reset(clearLocalStorage = false): void {
    this.brandId = '';
    this.marketId = '';
    this.modelId = '';
    this.sessionId = '';
    this.userId = '';
    this.campaign = '';
    this.preset = '';
    this.performanceConsent = false;
    this.marketingConsent = false;
    this.initialized = false;
    this.interactionCount = 0;

    if (clearLocalStorage && globalThis.localStorage) {
      globalThis.localStorage.removeItem(USER_ID_KEY);
    }
  }

  /** Expose initialization status — useful for tests and conditional logic. */
  get isInitialized(): boolean {
    return this.initialized;
  }

  // --------------------------------------------------------------------------
  // Private helpers
  // --------------------------------------------------------------------------

  private hasConsent(event: EventName): boolean {
    const category = EVENT_CONSENT[event];
    switch (category) {
      case 'necessary':
        return true;
      case 'performance':
        return this.performanceConsent;
      case 'marketing':
        return this.marketingConsent;
      default:
        return false;
    }
  }

  private buildPayload(event: string, data?: Record<string, unknown>): TrackingPayload {
    const payload: TrackingPayload = {
      brandid: this.brandId,
      marketid: this.marketId,
      modelid: this.modelId,
      sessionid: this.sessionId,
      userid: this.userId,
      event,
      timestamp: Date.now(),
      page: `/${this.brandId}/${this.marketId}/${this.modelId}`,
    };
    if (this.campaign) payload.campaign = this.campaign;
    if (this.preset) payload.preset = this.preset;
    if (data) payload.data = data;
    return payload;
  }

  /**
   * Fire-and-forget wrapper — callers stay synchronous, retries happen silently.
   *
   * Retry strategy:
   *   • 504 Gateway Timeout → likely AWS Lambda cold start → retry up to
   *     SEND_MAX_RETRIES times with SEND_RETRY_DELAY_MS between attempts.
   *   • Network error       → same retry logic (transient connectivity blip).
   *   • Any other non-ok status (4xx, 5xx ≠ 504) → log in dev, silent in prod.
   */
  private send(payload: TrackingPayload): void {
    const dev = this.isDev();

    if (dev) {
      console.group(
        `%c[IMS Tracking] 📤 ${payload.event} → SENT`,
        'color: #4ade80; font-weight: bold',
      );
      console.log('Payload:', payload);
      if (payload.data) console.log('Data:', payload.data);
      console.log('Endpoint:', API_ENDPOINT);
      console.groupEnd();
    }

    this.sendWithRetry(payload, SEND_MAX_RETRIES, SEND_RETRY_DELAY_MS).catch(() => {
      // sendWithRetry never rejects — this is just a safety net.
    });
  }

  private async sendWithRetry(
    payload: TrackingPayload,
    retriesLeft: number,
    delayMs: number,
  ): Promise<void> {
    const dev = this.isDev();
    const attempt = SEND_MAX_RETRIES - retriesLeft + 1;

    try {
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: AUTH_HEADER,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) return;

      // 504 = API Gateway timeout, very likely an AWS Lambda cold start.
      // Retry after a short delay — the Lambda should be warm by then.
      if (res.status === 504 && retriesLeft > 0) {
        if (dev) {
          console.warn(
            `%c[IMS Tracking] ⏳ ${payload.event} → 504 (cold start?), retry ${attempt}/${SEND_MAX_RETRIES} in ${delayMs}ms`,
            'color: #fbbf24; font-weight: bold',
          );
        }
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
        return this.sendWithRetry(payload, retriesLeft - 1, delayMs);
      }

      if (dev) {
        console.error(
          `%c[IMS Tracking] ❌ ${payload.event} → HTTP ${res.status} (${res.statusText})`,
          'color: #f87171; font-weight: bold',
        );
      }
    } catch (err) {
      // Network error (offline, DNS failure, etc.)
      if (retriesLeft > 0) {
        if (dev) {
          console.warn(
            `%c[IMS Tracking] ⚠️ ${payload.event} → network error, retry ${attempt}/${SEND_MAX_RETRIES} in ${delayMs}ms`,
            'color: #fbbf24; font-weight: bold',
          );
        }
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
        return this.sendWithRetry(payload, retriesLeft - 1, delayMs);
      }
      // Silent fail in prod — tracking errors never affect the user.
      if (dev) {
        console.error('[IMS Tracking] Network error (no retries left):', err);
      }
    }
  }

  private isDev(): boolean {
    try {
      // Vite injects import.meta.env.DEV as boolean true/false at build time.
      // Use !! to coerce any truthy value (boolean true OR string 'true').
      if ((import.meta as { env?: Record<string, unknown> }).env?.DEV) return true;
      // Allow verbose logging on production via ?ims_debug=1 (QA / payload inspection).
      const search = globalThis.location?.search ?? '';
      return new URLSearchParams(search).get('ims_debug') === '1';
    } catch {
      return false;
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

/**
 * Singleton tracker instance.
 *
 * Import and use anywhere:
 *   import { tracker, EVENTS } from '../services/tracking';
 *   tracker.track(EVENTS.COLOR_CHANGED, { colorId: 'mythos-black' });
 */
export const tracker = new TrackingService();
